import React, { FC, useEffect, useState } from 'react';
import { CHECKOUT_FORM_FIELDS, CHECKOUT_FORM_FIELDS_NAMES } from '../../../constants/merchant/checkout-form';
import { UploadLogo } from '../../developers/widget-configuration/UploadLogo';
import { useTranslation } from 'react-i18next';
import { getCroppedImg } from '../../../utils/imageCrop';
import { Area } from 'react-easy-crop';
import { ImageCropper } from '../../common/ImageCropper';
import { ModalWindow } from '../../modal/ModalWindow';
import { sendSentryMessage } from '../../../utils/sentry-helper';

interface Props {
	formik: any;
	uploadedFavicon: string | undefined;
	setUploadedFavicon: (value: string | undefined) => void;
}

export const FaviconLoader: FC<Props> = (props): JSX.Element => {
	const {
		formik,
		uploadedFavicon,
		setUploadedFavicon,
	} = props;

	const { t } = useTranslation();
	const [pixelCrop, setPixelCrop] = useState<Area | undefined>();
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [isDisableFavicon, setIsDisableFavicon] = useState<boolean>(false);
	const [previewFavicon, setPreviewFavicon] = useState<string | undefined>();
	const [selectedFavicon, setSelectedFavicon] = useState(undefined);

	useEffect(() => {
		if (!selectedFavicon) {
			setPreviewFavicon(undefined);
			return;
		}

		const objectUrl = URL.createObjectURL(selectedFavicon);
		// @ts-ignore
		setPreviewFavicon(objectUrl);

		return (): void => URL.revokeObjectURL(objectUrl);
	}, [selectedFavicon]);

	const onModalSaveFavicon = async (): Promise<void> => {
		try {
			const { file, fileURL } = await getCroppedImg({
				imageSrc: previewFavicon,
				pixelCrop,
				maxWidth: 512
			});
			formik.setFieldValue(CHECKOUT_FORM_FIELDS.FAVICON, file);
			setUploadedFavicon(fileURL);
			setIsDisableFavicon(true);
		} catch (e) {
			sendSentryMessage({ name: `Save favicon error`, context: e });
			console.error(e);
		}
		setIsModalOpen(false);
	};

	const modalClose = (): void => {
		setIsModalOpen(false);
	};

	const onSelectFavicon = (e): void => {
		if (!e.target.files || e.target.files.length === 0) {
			setSelectedFavicon(undefined);
			return;
		}
		const files = e.target.files;
		const reader = new FileReader();
		reader.readAsDataURL(files[0]);

		reader.onload = function (): void {
		};

		setIsModalOpen(true);
		setSelectedFavicon(files[0]);
	};

	const handleEditFavicon = (): void => {
		if (previewFavicon) {
			setIsModalOpen(true);
		}
	};

	const handleDeleteFavicon = (): void => {
		formik.setFieldValue(CHECKOUT_FORM_FIELDS.FAVICON, '');
		setUploadedFavicon(undefined);
		setPreviewFavicon(undefined);
	};

	return (
		<>
			<UploadLogo
				uploadedFile={uploadedFavicon}
				formik={formik}
				isDisable={isDisableFavicon}
				onDeleteFile={handleDeleteFavicon}
				onSelectFile={onSelectFavicon}
				onEditFile={handleEditFavicon}
				isAdmin
				label={t(CHECKOUT_FORM_FIELDS_NAMES.FAVICON)}
				fieldName={CHECKOUT_FORM_FIELDS.FAVICON}
				additionalClass="col-6 col-sm-5"
				imageAdditionalClass="image-size"
				isWidgetPage
			/>
			<ModalWindow
				isOpen={isModalOpen}
				modalTitle={t('COLOR_MODULE.CROP_TITLE')}
				confirmButtonText={t('COLOR_MODULE.SAVE')}
				onClose={modalClose}
				onCancel={modalClose}
				onConfirm={onModalSaveFavicon}
				contentClassName="position-relative"
				content={
					<ImageCropper
						image={previewFavicon}
						setPixelCrop={setPixelCrop}
						aspect={1/1}
					/>
				}
			/>
		</>
	);
};
